import type { CSSProperties, ElementType, HTMLAttributes, ReactNode } from "react";

type BaseProps<T extends ElementType> = {
  as?: T;
  children?: ReactNode;
  style?: CSSProperties;
} & Omit<HTMLAttributes<HTMLElement>, "style" | "children">;

export function Box<T extends ElementType = "div">({ as, style, children, ...rest }: BaseProps<T>) {
  const Component = (as ?? "div") as ElementType;
  return <Component style={style} {...rest}>{children}</Component>;
}

interface FlexProps<T extends ElementType = "div"> extends BaseProps<T> {
  align?: CSSProperties["alignItems"];
  justify?: CSSProperties["justifyContent"];
  direction?: CSSProperties["flexDirection"];
  gap?: CSSProperties["gap"];
  wrap?: CSSProperties["flexWrap"];
}

export function Flex<T extends ElementType = "div">({
  as,
  style,
  children,
  align,
  justify,
  direction,
  gap,
  wrap,
  ...rest
}: FlexProps<T>) {
  const Component = (as ?? "div") as ElementType;
  return (
    <Component
      style={{
        display: "flex",
        alignItems: align,
        justifyContent: justify,
        flexDirection: direction,
        gap,
        flexWrap: wrap,
        ...style,
      }}
      {...rest}
    >
      {children}
    </Component>
  );
}

interface TextProps<T extends ElementType = "span"> extends BaseProps<T> {
  size?: string;
}

const textSizeMap: Record<string, string> = {
  "1": "0.75rem",
  "2": "0.875rem",
  "3": "1rem",
};

export function Text<T extends ElementType = "span">({ as, size, style, children, ...rest }: TextProps<T>) {
  const Component = (as ?? "span") as ElementType;
  return (
    <Component
      style={{
        fontSize: size ? textSizeMap[size] : undefined,
        ...style,
      }}
      {...rest}
    >
      {children}
    </Component>
  );
}
